import { STUDIO_MODES } from '@liveforge/shared';
import { useStudioStore } from '../../stores/studio-store';
import { DuplicateIcon, PencilIcon, PlusIcon, TrashIcon } from './icons';

/**
 * The mode switch, on the dock it governs.
 *
 * It used to sit in the topbar, the length of the screen away from the thing
 * it changes. What a click on a scene *does* is decided here, so it belongs
 * next to the scenes - with the consequence spelled out underneath rather than
 * hidden in a tooltip the director finds after going on air.
 */
function ModeSwitch() {
  const mode = useStudioStore((state) => state.mode);
  const setMode = useStudioStore((state) => state.setMode);

  return (
    <div className="mode-row">
      <div className="mode-switch" role="group" aria-label="Studio mode">
        {STUDIO_MODES.map((option) => (
          <button
            key={option}
            type="button"
            className={`mode-switch__option ${mode === option ? 'mode-switch__option--on' : ''}`}
            onClick={() => setMode(option)}
          >
            {option === 'quick' ? 'Quick' : 'Studio'}
          </button>
        ))}
      </div>
      <span className="mode-hint">
        {mode === 'quick'
          ? 'A click puts the scene on air immediately.'
          : 'A click stages the scene in preview. TAKE puts it on air.'}
      </span>
    </div>
  );
}

/**
 * The scene list.
 *
 * What a click does depends on the mode, and the strip says so rather than
 * leaving the director to find out on air: in quick mode a click *is* the cut,
 * in studio mode it only loads the scene into the preview.
 */
export function SceneStrip() {
  const mode = useStudioStore((state) => state.mode);
  const scenes = useStudioStore((state) => state.scenes);
  const activeSceneId = useStudioStore((state) => state.activeSceneId);
  const previewSceneId = useStudioStore((state) => state.previewSceneId);
  const chooseScene = useStudioStore((state) => state.chooseScene);
  const addScene = useStudioStore((state) => state.addScene);
  const duplicateScene = useStudioStore((state) => state.duplicateScene);
  const removeScene = useStudioStore((state) => state.removeScene);
  const renameScene = useStudioStore((state) => state.renameScene);

  return (
    <section className="dock">
      <div className="dock__header">
        <h2>Scenes</h2>
        <button type="button" className="dock__add" onClick={addScene}>
          <PlusIcon />
          Add
        </button>
      </div>

      <ModeSwitch />

      <ul className="scene-list">
        {scenes.map((scene) => {
          const live = scene.id === activeSceneId;
          const previewed = scene.id === previewSceneId && mode === 'studio';
          return (
            <li key={scene.id}>
              <div
                className={`scene-row ${live ? 'scene-row--live' : ''} ${
                  previewed ? 'scene-row--preview' : ''
                }`}
              >
                <button
                  type="button"
                  className="scene-row__pick"
                  onClick={() => chooseScene(scene.id)}
                  title={
                    mode === 'quick' ? 'Take this scene to air now' : 'Load this scene into preview'
                  }
                >
                  <span className="scene-row__name" title={scene.name}>
                    {scene.name}
                  </span>
                  {previewed ? (
                    <span className="scene-row__tally scene-row__tally--preview">PREVIEW</span>
                  ) : null}
                  {live ? <span className="scene-row__tally">ON AIR</span> : null}
                </button>

                <div className="scene-row__actions">
                  <button
                    type="button"
                    className="icon-button"
                    title="Rename"
                    onClick={() => {
                      const name = window.prompt('Scene name', scene.name)?.trim();
                      if (name) renameScene(scene.id, name);
                    }}
                  >
                    <PencilIcon />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    title="Duplicate"
                    onClick={() => duplicateScene(scene.id)}
                  >
                    <DuplicateIcon />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    title={
                      scenes.length <= 1 ? 'The last scene cannot be deleted' : 'Delete this scene'
                    }
                    disabled={scenes.length <= 1}
                    onClick={() => removeScene(scene.id)}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
